<template>
  <v-dialog
    content-class="add-cloud-account-popup"
    :value="internalValue"
    :width="width"
    @click:outside="onEscapeClicked"
    @keydown.esc="onEscapeClicked"
  >
    <v-card class="popup-wrapper">
      <span class="escape-link d-inline-block" @click="onEscapeClicked"> X {{ $t('newProject.esc') }} </span>
      <div class="content">
        <div class="add-title">
          {{ $t('accounts.add.cloud.account.title') }}
        </div>
        <div class="add-item-form" :style="`width: ${width}`">
          <div class="item-inputs" v-for="(value, index) in cloudAccountItems" :key="index">
            <div class='choose-provider-row-section'>
              <span class="choose-provider-row-section__title">{{ $t("accounts.add.cloud.account.provider") }}</span>
              <v-autocomplete
                class-name="provider-select"
                :placeholder="$t('select')"
                :items='listOfProviders'
                :rules='validationRules'
                :append-icon="'expand_more'"
                v-model='cloudAccountItems[index].provider'
                dense
                outlined
                autofocus
                return-object
              />
            </div>
            <div class="text-field-section" :class='{"not-valid": !isFormValid}'>
              <div class='label'>
                <custom-tooltip class="label__tooltip" placement="right" popper-class="light-tooltip">
                  <div slot='buttonContent'>
                    <span class="text-field-section__title">{{ labelsPerProvider[cloudAccountItems[index].provider] || $t('accounts.add.cloud.account.label') }}</span>
                    <v-icon class="material-icons-round">help</v-icon>
                  </div>
                  <div slot="content">
                    <slot name="tooltip">
                      {{ placeholdersPerProvider[cloudAccountItems[index].provider] }}
                    </slot>
                  </div>
                </custom-tooltip>
              </div>
              <text-field
                :ref="`url_${index}`"
                type="text"
                v-model="cloudAccountItems[index].account"
                class="login"
                :rules="getValidationRules(cloudAccountItems[index].provider)"
                :placeholder="placeholdersPerProvider[cloudAccountItems[index].provider] || $t('accounts.add.cloud.account.placeholder')"
              />
            </div>
            <div class='choose-organization-row-section'>
              <span class="choose-organization-row-section__title">{{ $t("accounts.add.cloud.account.organization") }}</span>
              <v-autocomplete
                class-name="organization-select"
                :placeholder="$t('select')"
                :ref="`org_${index}`"
                :items='organizationsItems'
                item-text='name'
                item-value='id'
                :append-icon="'expand_more'"
                v-model='cloudAccountItems[index].organization'
                dense
                outlined
                autofocus
                return-object
              />
            </div>
            <div class='delete-row-section'>
              <custom-tooltip class='delete-row-section__tooltip' placement="right" popper-class="light-tooltip">
                <div slot='buttonContent'>
                    <span
                      class="delete-icon material-icons"
                      @click="deleteCurrentItem(index)"
                    >
                        highlight_off
                    </span>
                </div>
                <div slot="content">
                  <slot name="tooltip">
                    {{$t('delete')}}
                  </slot>
                </div>
              </custom-tooltip>
            </div>
          </div>
          <div class="add-new-item-section">
            <div class="add-new-item-header">
              <label  class="add-new-item-add-button"  @click="addItem">
                <span class="plus">+</span>{{ $t('add.new.email.popup.button') }}
              </label>
            </div>
          </div>
          <div class="buttons">
            <v-btn class="save-button" :disabled="saveButtonDisabled" @click="onSaveClick" :loading='loading'>
              {{ $t('save') }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import DisableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import TextField from '@/components/common/TextField.vue';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import { rules } from '@/utils/validationUtils';
import { CONFIGURATION } from '@/constants/configuration';
import { mapGetters } from 'vuex';

export default {
  name: 'AddCloudAccountPopup',
  components: { CustomTooltip, TextField },
  mixins: [DisableOuterScrollOnPopupVisible],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      required: false,
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isFormValid: false,
      cloudAccountItems: [{
        account: '',
        tld: '',
        subdomain: '',
        provider: CONFIGURATION.SOURCE_TYPE.aws.toUpperCase(),
        organization: { name: this.companyData?.name || '', id: this.companyData?.id || '' },
        placeholder: this.$t('accounts.add.cloud.account.placeholder.aws'),
      }],
    };
  },
  methods: {
    checkAccountValidation(item) {
      if (item.account?.length && item.provider?.length) {
        if (item.provider.toLowerCase() === CONFIGURATION.SOURCE_TYPE.aws) {
          return this.validationRulesOptions[CONFIGURATION.SOURCE_TYPE.aws.toUpperCase()](item.account);
        }
        if (item.provider.toLowerCase() === CONFIGURATION.SOURCE_TYPE.azure) {
          return this.validationRulesOptions[CONFIGURATION.SOURCE_TYPE.azure.toUpperCase()](item.account);
        }
        return this.validationRulesOptions[CONFIGURATION.SOURCE_TYPE.gcp.toUpperCase()](item.account);
      }
      return false;
    },
    onEscapeClicked() {
      this.internalValue = false;
    },
    deleteCurrentItem(index) {
      if (this.cloudAccountItems.length > 1) {
        this.cloudAccountItems.splice(index, 1);
      }
    },
    onSaveClick() {
      const newItems = [];
      this.cloudAccountItems.forEach(item => {
        newItems.push(
          {
            provider: item.provider,
            url: item.account,
            tld: item.account,
            companyId: item.organization?.id || this.companyData?.id,
          },
        );
      });
      this.$emit('addCloudAccounts', { items: newItems, configType: CONFIGURATION.CONFIG_TYPES.CLOUD_ACCOUNT });
      this.onEscapeClicked();
    },
    addItem() {
      this.cloudAccountItems.push({
        account: '',
        tld: '',
        subdomain: '',
        provider: CONFIGURATION.SOURCE_TYPE.aws.toUpperCase(),
        organization: { name: this.companyData?.name || '', id: this.companyData?.id || ''},
        placeholder: this.placeholdersPerProvider[CONFIGURATION.SOURCE_TYPE.aws.toUpperCase()],
      });
    },
    getValidationRules(provider) {
      if (!provider?.length || !Object.values(CONFIGURATION.SOURCE_TYPE).includes(provider.toLowerCase())) {
        return [...this.validationRules];
      }
      return [...this.validationRules, ...[this.validationRulesOptions[provider]]];
    },
  },
  watch: {
    cloudAccountItems: {
      handler() {
        const validationArray = [];
        let validAssociation = false;
        this.cloudAccountItems.forEach(item => {
          if (item) {
            const validationForCurrentRef = this.validationRules?.map(validationRule => {
              let result = false;
              if (item.account !== '') {
                result = validationRule(item.account);
              }
              return result;
            });
            const booleanValue = validationForCurrentRef.every(e => ((typeof (e) === 'boolean') && (e === true)));
            validationArray.push(booleanValue);
          }
        });
        if (this.cloudAccountItems.length > 1 || this.cloudAccountItems[0].account !== '') {
          validAssociation = this.cloudAccountItems.every(item => this.checkAccountValidation(item));
        }
        this.isFormValid = !validationArray.includes(false) && validAssociation;
      },
      deep: true,
    },
    internalValue() {
      if (this.internalValue) {
        this.isFormValid = false;
        this.cloudAccountItems = [{
          account: '',
          tld: '',
          subdomain: '',
          provider: CONFIGURATION.SOURCE_TYPE.aws.toUpperCase(),
          organization: { name: this.companyData?.name || '', id: this.companyData?.id || '' },
          placeholder: this.$t('accounts.add.cloud.account.placeholder.aws'),
        }];
      }
    },
  },
  computed: {
    ...mapGetters(['organizations', 'companyData']),
    placeholdersPerProvider() {
      return {
        [CONFIGURATION.SOURCE_TYPE.aws.toUpperCase()]: this.$t('accounts.add.cloud.account.placeholder.aws'),
        [CONFIGURATION.SOURCE_TYPE.azure.toUpperCase()]: this.$t('accounts.add.cloud.account.placeholder.azure'),
        [CONFIGURATION.SOURCE_TYPE.gcp.toUpperCase()]: this.$t('accounts.add.cloud.account.placeholder.gcp'),
      };
    },
    labelsPerProvider() {
      return {
        [CONFIGURATION.SOURCE_TYPE.aws.toUpperCase()]: this.$t('accounts.add.cloud.account.label.aws'),
        [CONFIGURATION.SOURCE_TYPE.azure.toUpperCase()]: this.$t('accounts.add.cloud.account.label.azure'),
        [CONFIGURATION.SOURCE_TYPE.gcp.toUpperCase()]: this.$t('accounts.add.cloud.account.label.gcp'),
      };
    },
    organizationsItems() {
      return this.organizations.map(org => ({
        id: org.id,
        name: org.name,
      })).sort((a, b) => (a.name > b.name ? 1 : -1));
    },
    saveButtonDisabled() {
      return !this.isFormValid;
    },
    validationRulesOptions() {
      return {
        ...rules,
        AWS: v => !!v && /^[0-9]+$/.test(v) && `${v}`.length === 12,
        AZURE: v => !!v && !v.includes(' ') && /^[a-zA-Z0-9-_]+$/.test(v),
        GCP: v => !!v && !v.includes(' ') && /^[a-zA-Z0-9-_]+$/.test(v),
        cloudAccount: v => !!v,
      };
    },
    listOfProviders() {
      return [CONFIGURATION.SOURCE_TYPE.aws.toUpperCase(), ...Object.values(CONFIGURATION.SOURCE_TYPE).map(item => item.toUpperCase())];
    },
    validationRules() {
      return [this.validationRulesOptions.cloudAccount];
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    width() {
      return 750;
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.add-cloud-account-popup {
  .v-icon.v-icon {
    color: var(--text-secondary);
    font-size: rem(22);
    font-weight: lighter;
  }
  .v-alert__icon.v-icon {
    color: var(--brand-primary);
  }
  .v-autocomplete__content.v-menu__content {
    width: rem(243);
  }
  .v-menu__content .v-list-item__title {
    text-transform: unset;
    font-size: $font-size-md;
  }
  .v-text-field {
    margin-top: $space-0;
  }
  .v-text-field fieldset, .v-text-field .v-input__control {
    color: inherit;
    border-radius: $radius-sm;
  }
}
</style>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.add-cloud-account-popup {
  .add-new-item-section {
    padding: $space-0;
    .add-new-item-header {
      display: flex;
      justify-content: space-between;

      .add-new-item-add-button {
        color: var(--brand-primary);
        text-transform: uppercase;
        font-size: $font-size-sm;
        font-weight: 500;
        letter-spacing: 1.07px;
        line-height: $space-4;
        text-align: center;
        margin-right: $space-3;
        cursor: pointer;

        .plus {
          font-size: $font-size-md;
        }
      }
    }
  }
  .item-inputs {
    flex: 50%;
    display: flex;
    align-items: center;
    .text-field-section {
      flex: 0 0 rem(252);
      margin-right: 2%;
      align-self: center;
      &__title {
        font-family: $font-family-regular;
        font-size: $font-size-xs;
        letter-spacing: 0.8px;
        line-height: $font-size-xs;
        margin-bottom: $space-1-5;
        color: var(--text-secondary);
      }
      .material-icons-round {
        margin-left: $space-1;
        font-size: $font-size-base;
      }
    }
    .choose-provider-row-section {
      align-self: center;
      margin-right: 2%;
      &__title {
        font-family: $font-family-regular;
        font-size: $font-size-xs;
        letter-spacing: 0.8px;
        line-height: $font-size-xs;
        margin-bottom: $space-1-5;
        color: var(--text-secondary);
      }
    }
    .choose-organization-row-section {
      align-self: center;
      &__title {
        font-family: $font-family-regular;
        font-size: $font-size-xs;
        letter-spacing: 0.8px;
        line-height: $font-size-xs;
        margin-bottom: $space-1-5;
        color: var(--text-secondary);
      }
    }
    .delete-row-section{
      flex:1;
      margin-top: $space-5;
      &__tooltip {
        background-color: transparent;
      }
    }
    .domain-select {
      flex:1;
      max-width: 20%;
    }
    .delete-icon {
      color: var(--severity-critical);
      cursor: pointer;
      font-size: $font-size-md;
      margin-left: $space-1-5;
      margin-bottom: $space-1-5;
      font-weight: bolder;
    }

    .dummy-icon {
      width: $space-4;
      height: $space-4;
    }
  }
  .error {
    background: transparent;
    text-transform: capitalize;
    color: var(--severity-critical);
    font-size: $font-size-xs;
    font-weight: 600;
    letter-spacing: 1.12px;
    line-height: $space-4;
    margin-bottom: calc(#{$space-2} + #{$space-0-5});
    margin-top: calc(#{$space-2} + #{$space-0-5});
  }
  .popup-wrapper {
    max-width: rem(750);
    padding: $space-8 $space-10;
    .escape-link {
      float: right;
      cursor: pointer;
      margin-left: $space-5;
      text-align: right;
      font-size: $font-size-sm;
      text-transform: uppercase;
      text-decoration: none;
      height: $space-4;
      color: var(--brand-primary);
      font-family: $font-family-medium;
      font-weight: 500;
      letter-spacing: 1.1px;
      line-height: $space-4;
    }
    .content {
      .add-item-form {
        margin-top: rem(10);
      }
      .add-title {
        text-transform: uppercase;
        color: var(--text-primary);
        font-size: $font-size-base;
        font-weight: 600;
        letter-spacing: 1.12px;
        line-height: $space-4;
        margin-bottom: $space-4;
      }
    }
    .buttons {
      text-align: right;
      padding-top: rem(30);
      padding-right: rem(19);

      .save-button {
        height: $space-8;
        border-radius: $radius-sm;
        background-color: var(--brand-primary);
        box-shadow: $shadow-sm;
        color: var(--text-inverse);
        font-size: $font-size-sm;
        font-weight: 500;
        letter-spacing: 1.07px;
        line-height: $space-4;
        text-align: center;
        margin-left: $space-2;
      }
    }
  }
}
</style>
